/**
 * The public page for one sound — what creators open from a brief, and where old adstudios.beatly.com track links
 * land. No login: anyone with the link can listen, read the details and download it. Two kinds of link:
 *   /share/sound/:id?token=…  an Audio Studio sound shared by link (GET /public/tracks/{id}?token=; attaching a
 *                             sound to a brief turns its link on)
 *   /sound/:id                a track from Beatly's curated library (public by id)
 * Only this one-track page is public; the library itself stays behind a login (Omni → Audio Studio → Library).
 */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, Lock, Music2 } from 'lucide-react';
import { audioStudioApi, downloadUrl } from '@/api/audioStudioApi';
import beatlyLogotype from '@/cowork/mobileReview/beatly-logotype-white.svg';
import { capitalize, formatClock } from './format';
import { PUBLIC_SOUND_LICENSE } from './rights';

export function PublicSoundPage({ source = 'studio' }: { source?: 'studio' | 'library' }) {
  const { id = '' } = useParams<{ id: string }>();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);
  const library = source === 'library';
  const q = useQuery({
    queryKey: ['audio-studio', 'public', source, id, token],
    queryFn: () => (library ? audioStudioApi.publicLibraryTrack(id) : audioStudioApi.publicTrack(id, token)),
    enabled: Boolean(id && (library || token)),
    retry: false,
  });
  useEffect(() => { if (q.data?.title) document.title = `${q.data.title} · Beatly`; }, [q.data?.title]);

  const missingToken = !library && !token;
  if (id && !missingToken && q.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#111318] text-[13px] text-[#98a2b3]"><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Loading…</div>;
  }
  if (!id || missingToken || q.isError || !q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8f9fc] px-6">
        <div className="max-w-md rounded-2xl border border-[#eaecf0] bg-white p-8 text-center shadow-sm">
          <Lock className="mx-auto h-6 w-6 text-[#98a2b3]" aria-hidden />
          <div className="mt-3 text-[16px] font-semibold text-[#101828]">This sound is not available</div>
          <div className="mt-1.5 text-[13px] text-[#667085]">
            {missingToken ? 'This link is incomplete.' : (q.error as Error | null)?.message || 'The link may have been reset or turned off.'}
          </div>
          <div className="mt-4 text-[11.5px] text-[#98a2b3]">
            {library ? 'The track may have been removed from the library.' : 'Ask whoever sent you the link to turn link sharing back on in Beatly.'}
          </div>
        </div>
      </div>
    );
  }

  const t = q.data;
  const meta = [t.artist, capitalize(t.genre), capitalize(t.mood), t.tempo ? `${capitalize(t.tempo)} tempo` : null,
    t.duration_s ? formatClock(t.duration_s) : null].filter(Boolean);
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-[#1b0730] via-[#111318] to-[#111318] text-white">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3">
        <a href="https://beatly.com" target="_blank" rel="noreferrer" aria-label="Beatly">
          <img src={beatlyLogotype} alt="Beatly" className="h-5 w-auto" />
        </a>
        <span className="min-w-0 flex-1" />
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white">{library ? 'Beatly library' : 'Shared sound'}</span>
      </div>
      <main className="flex flex-1 items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur sm:p-7">
          <div className="aspect-square w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#be43ff] to-[#3b0764]">
            {t.cover_url ? <img src={t.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Music2 className="h-16 w-16 text-white/70" aria-hidden /></div>}
          </div>
          <h1 className="mt-4 text-[22px] leading-tight" style={{ fontWeight: 700 }}>{t.title || 'Untitled'}</h1>
          {meta.length > 0 && <div className="mt-1 text-[13px] text-white/60">{meta.join(' · ')}</div>}
          {t.audio_url && (
            <>
              <audio controls preload="metadata" src={t.audio_url} className="mt-4 w-full" />
              <a
                href={downloadUrl(t.audio_url)}
                download
                className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#a9f600] text-[14px] text-[#101828] hover:bg-[#b8ff1a]"
                style={{ fontWeight: 600 }}
              >
                <Download className="h-4 w-4" aria-hidden /> Download
              </a>
            </>
          )}
          {/* One line of rights up front; the full terms open on demand, so the card fits one screen. */}
          <div className="mt-4 text-[12.5px] leading-[18px] text-white/70">
            <p>{PUBLIC_SOUND_LICENSE.summary}</p>
            <details className="group mt-1.5">
              <summary className="cursor-pointer list-none text-white/50 underline-offset-2 hover:text-white/80 hover:underline [&::-webkit-details-marker]:hidden">
                {PUBLIC_SOUND_LICENSE.title}
              </summary>
              <ul className="mt-2 space-y-1.5">
                {PUBLIC_SOUND_LICENSE.items.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#a9f600]" />
                    {item}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      </main>
    </div>
  );
}
